import React, { useState, useEffect } from 'react';
import supabase from '../../supabase';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiTrash2, FiCheck, FiLoader, FiMail, FiCalendar, FiExternalLink } from 'react-icons/fi';

const NewsletterDashboard = () => {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    subject: '',
    body_html: ''
  });

  const fetchNewsletters = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('newsletters_20240320')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNewsletters(data || []);
    } catch (err) {
      console.error('Error fetching newsletters:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsletters();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.body_html) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('newsletters_20240320')
        .insert([{
          subject: formData.subject,
          body_html: formData.body_html,
          slug: 'temp-' + Date.now() // Trigger will override this
        }]);

      if (error) throw error;

      setSuccess(true);
      setFormData({ subject: '', body_html: '' });
      fetchNewsletters();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving newsletter:', err);
      alert('Error saving newsletter: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this newsletter?')) return;

    try {
      const { error } = await supabase
        .from('newsletters_20240320')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchNewsletters();
    } catch (err) {
      console.error('Error deleting newsletter:', err);
      alert('Error deleting newsletter: ' + err.message);
    }
  };

  return (
    <div className="space-y-12">
      {/* Creation Form */}
      <section className="bg-white rounded-[40px] border border-stone-200 p-8 md:p-12 shadow-sm">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600">
            <SafeIcon icon={FiPlus} className="text-2xl" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-stone-900 uppercase tracking-tight">Archive New Issue</h2>
            <p className="text-stone-500 font-bold uppercase tracking-widest text-[8px]">Paste HTML from your email provider</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-stone-400 ml-1">Subject Line</label>
            <input
              required
              type="text"
              className="w-full px-6 py-4 bg-stone-50 border border-stone-100 rounded-2xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm font-bold"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="e.g. The Wild Report: Summer Expeditions Are Here"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-stone-400 ml-1">Full HTML Body</label>
            <textarea
              required
              rows={12}
              className="w-full px-6 py-4 bg-stone-50 border border-stone-100 rounded-2xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all text-sm font-mono"
              value={formData.body_html}
              onChange={(e) => setFormData({ ...formData, body_html: e.target.value })}
              placeholder="Paste your exported HTML here..."
            />
          </div>

          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 md:flex-none px-12 py-5 bg-stone-900 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-stone-800 transition-all flex items-center justify-center gap-3 disabled:opacity-50 shadow-xl shadow-stone-900/10"
            >
              {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : success ? <><SafeIcon icon={FiCheck} /> Saved to Archive</> : 'Archive Newsletter'}
            </button>
          </div>
        </form>
      </section>

      {/* Existing Issues */}
      <section>
        <div className="flex items-center gap-4 mb-8 px-4">
          <div className="w-10 h-10 bg-stone-100 rounded-xl flex items-center justify-center text-stone-600">
            <SafeIcon icon={FiMail} />
          </div>
          <div>
            <h2 className="text-2xl font-black text-stone-900 uppercase tracking-tight">Archived Issues</h2>
            <p className="text-stone-500 font-bold uppercase tracking-widest text-[8px]">History of sent newsletters</p>
          </div>
        </div>

        <div className="grid gap-4">
          {loading ? (
            <div className="py-24 text-center bg-white rounded-[40px] border border-stone-200 border-dashed">
              <FiLoader className="animate-spin mx-auto text-stone-300 text-2xl" />
            </div>
          ) : newsletters.length > 0 ? (
            newsletters.map(issue => (
              <div key={issue.id} className="bg-white rounded-[24px] border border-stone-200 p-6 flex flex-col md:flex-row items-center justify-between gap-6 hover:border-stone-300 transition-all">
                <div className="flex items-center gap-6">
                  <div className="w-12 h-12 bg-stone-50 rounded-xl flex items-center justify-center text-stone-300">
                    <SafeIcon icon={FiCalendar} />
                  </div>
                  <div>
                    <h3 className="font-black text-stone-900 uppercase tracking-tight">{issue.subject}</h3>
                    <p className="text-stone-400 font-bold uppercase tracking-widest text-[8px]">
                      {new Date(issue.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 w-full md:w-auto">
                  <a
                    href={`/newsletters/${issue.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 md:flex-none px-6 py-3 bg-stone-100 text-stone-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-stone-200 transition-all flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiExternalLink} /> Preview
                  </a>
                  <button
                    onClick={() => handleDelete(issue.id)}
                    className="p-3 text-stone-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="py-24 text-center bg-white rounded-[40px] border border-stone-200 border-dashed text-stone-400 font-black uppercase text-xs">
              No newsletters in archive.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsletterDashboard;